import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { HERO_COPY, SITE } from '@/content/yawasCopy';

interface HeroSlide {
  id: string;
  sort_order: number;
  name: string;
  tag: string;
  description: string;
  detail: string;
  price: string;
  image_url: string;
  accent_color: string;
  accent_light: string;
  is_active: boolean;
}

const FALLBACK_SLIDES: HeroSlide[] = [
  {
    id: 'f1',
    sort_order: 1,
    name: 'Smash Burger',
    tag: 'Signature',
    description: 'Hausgemacht mit täglich frischen Rindfleisch-Patties und Pommes',
    detail: 'Frisch · Qualität · St. Gallen',
    price: 'ab CHF 15.–',
    image_url: '/food/smash-burger-slider-v2.png',
    accent_color: '#EF4444',
    accent_light: '#FECACA',
    is_active: true,
  },
  {
    id: 'f2',
    sort_order: 2,
    name: 'Kebab Dürüm',
    tag: 'Beliebt',
    description: 'Saftiges Kebabfleisch mit frischem Salat im gerösteten Fladenbrot',
    detail: 'Handgerollt · Frisch · Mit Zwiebeln & Petersilie',
    price: 'ab CHF 12.–',
    image_url: '/food/kebab-durum.png',
    accent_color: '#F59E0B',
    accent_light: '#FDE68A',
    is_active: true,
  },
  {
    id: 'f3',
    sort_order: 3,
    name: 'Kebab',
    tag: 'Klassiker',
    description: 'Saftiges Dönerfleisch im frischen Fladenbrot mit Salat und Sauce',
    detail: 'Frisch · Knusprig · Mit Haus-Sauce',
    price: 'ab CHF 11.–',
    image_url: '/food/kebab.png',
    accent_color: '#EF4444',
    accent_light: '#FECACA',
    is_active: true,
  },
  {
    id: 'f4',
    sort_order: 4,
    name: 'Big Smash Burger',
    tag: 'XXL',
    description: 'Grosser Smash Burger mit doppelter Portion und Pommes',
    detail: 'Extra Fleisch · Knusprig · Mit Pommes',
    price: 'ab CHF 20.–',
    image_url: '/food/smash-burger-hero.png',
    accent_color: '#10B981',
    accent_light: '#A7F3D0',
    is_active: true,
  },
];

const DEFAULT_BG = '/hero/yawas-exterior-bg-soft-v6.jpg';

const SLIDE_DURATION = 5000;

/** Render price with smaller CHF, preferring CHF before the amount. */
const PriceText = ({ price, currencyClassName = 'text-[0.72em]' }: { price: string; currencyClassName?: string }) => {
  const leading = price.match(/^(.*?)(CHF)\s+(.+)$/i);
  if (leading) {
    return (
      <>
        {leading[1]}
        <span className={currencyClassName}>{leading[2]}</span>
        {' '}
        {leading[3]}
      </>
    );
  }
  const trailing = price.match(/^(.*?)(\s*CHF)\s*$/i);
  if (!trailing) return <>{price}</>;
  return (
    <>
      {trailing[1]}
      <span className={currencyClassName}>{trailing[2]}</span>
    </>
  );
};

// SVG Timer Ring component
interface TimerRingProps {
  size: number;
  strokeWidth: number;
  color: string;
  progress: number; // 0 to 1
}

const TimerRing = ({ size, strokeWidth, color, progress }: TimerRingProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Clockwise (saat yönü): progress 1→0 olurken offset 0→circumference
  // rotate-90 ile 12 o'clock'tan başlatıyoruz, scaleX(-1) ile ayna alarak saat yönü yapıyoruz
  const offset = circumference * (1 - progress);

  return (
    <svg
      width={size}
      height={size}
      className="absolute inset-0"
      style={{ pointerEvents: 'none', transform: 'rotate(-90deg) scaleX(-1)' }}
    >
      {/* Track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={strokeWidth}
      />
      {/* Progress — saat yönünde (sağdan sola görünür) */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.1s linear, stroke 0.5s ease' }}
      />
    </svg>
  );
};

const HeroSection = () => {
  const [slides, setSlides] = useState<HeroSlide[]>(FALLBACK_SLIDES);
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [timerProgress, setTimerProgress] = useState(1);
  const animRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const { get } = useSiteSettings();

  const bgImage = get('hero_bg_url', DEFAULT_BG).trim() || DEFAULT_BG;

  useEffect(() => {
    const fetchSlides = async () => {
      const { data, error } = await supabase
        .from('hero_slides')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (!error && data && data.length > 0) {
        setSlides(data as HeroSlide[]);
        setActive(0);
      }
    };
    fetchSlides();
  }, []);

  const resetTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    setTimerProgress(1);
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    resetTimer();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 1 - elapsed / SLIDE_DURATION);
      setTimerProgress(remaining);
    }, 50);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      triggerChange((prev: number) => (prev + 1) % slides.length);
    }, SLIDE_DURATION);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length, active]);

  const triggerChange = useCallback((getNext: (prev: number) => number) => {
    setAnimating(true);
    if (animRef.current) clearTimeout(animRef.current);
    animRef.current = setTimeout(() => {
      setActive(getNext);
      setAnimating(false);
      resetTimer();
    }, 200);
  }, [resetTimer]);

  const goTo = useCallback((idx: number) => {
    if (idx === active || animating) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    triggerChange(() => idx);
  }, [active, animating, triggerChange]);

  const handlePrev = () => goTo((active - 1 + slides.length) % slides.length);
  const handleNext = () => goTo((active + 1) % slides.length);

  const current = slides[active] ?? FALLBACK_SLIDES[0];

  // Ring padding (gap between image edge and ring)
  const RING_PAD = 10;
  const STROKE = 4;

  // Mobile: image = 260px
  const mobileImgSize = 260;
  const mobileRingSize = mobileImgSize + RING_PAD * 2;

  // Desktop: image = 360px
  const desktopImgSize = 360;
  const desktopRingSize = desktopImgSize + RING_PAD * 2;

  return (
    <section className="relative w-full min-h-screen overflow-hidden flex items-center">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt="Hintergrund"
          className="h-full max-w-none object-cover w-[165%] md:w-[122%] object-[52%_top] md:object-left scale-[1.06] md:scale-[1.04] -translate-x-[12%] md:-translate-x-[20%]"
          style={{
            transformOrigin: 'center',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.38) 45%, rgba(0,0,0,0.22) 100%)' }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.20) 100%)' }}
        />
      </div>

      {/* Accent blob — desktop only */}
      <div
        className="hidden lg:block absolute right-0 top-0 w-[55%] h-full opacity-10 transition-all duration-700"
        style={{ background: `radial-gradient(ellipse at 80% 50%, ${current.accent_color} 0%, transparent 70%)` }}
      />

      {/* ── MOBILE LAYOUT ── */}
      <div className="lg:hidden relative z-10 w-full flex flex-col items-center pt-36 pb-10 px-5">
        <div
          className="flex flex-col items-center w-full"
          style={{ opacity: animating ? 0 : 1, transition: 'opacity 0.2s ease' }}
        >
          {/* Product image with timer ring */}
          <div
            className="relative mb-6 flex-shrink-0 mt-2"
            style={{ width: mobileRingSize, height: mobileRingSize }}
          >
            {/* Glow */}
            <div
              className="absolute inset-0 rounded-full blur-2xl opacity-35 transition-all duration-700"
              style={{ background: current.accent_color }}
            />
            {/* Timer ring — sits exactly on top of the image circle */}
            <TimerRing
              size={mobileRingSize}
              strokeWidth={STROKE}
              color={current.accent_color}
              progress={timerProgress}
            />
            {/* Image circle — inset by RING_PAD so ring doesn't overlap */}
            <div
              className="absolute rounded-full overflow-hidden border-[3px] shadow-2xl"
              style={{
                top: RING_PAD,
                left: RING_PAD,
                width: mobileImgSize,
                height: mobileImgSize,
                borderColor: current.accent_color,
              }}
            >
              <img
                src={current.image_url}
                alt={current.name}
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Price badge */}
            <div
              className="absolute -bottom-1 right-2 px-3.5 py-2 rounded-full text-black font-black text-sm shadow-xl z-10"
              style={{ background: current.accent_color }}
            >
              <PriceText price={current.price} currencyClassName="text-[0.78em] font-extrabold" />
            </div>
          </div>

          {/* Fixed-height text block */}
          <div className="flex flex-col items-center w-full" style={{ minHeight: 140 }}>
            <h1
              className="text-4xl font-black text-white text-center leading-tight mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {current.name}
            </h1>
            <p className="text-white/70 text-sm text-center leading-relaxed max-w-xs">
              {current.description}
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-3 mb-6 mt-4 w-full max-w-xs">
            <Link
              to="/menu"
              className="flex items-center justify-center gap-2 w-full px-8 py-3.5 rounded-full font-black text-base text-black transition-all duration-300 cursor-pointer whitespace-nowrap active:scale-95 shadow-2xl"
              style={{
                background: current.accent_color,
                boxShadow: `0 6px 24px ${current.accent_color}55`,
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-restaurant-line text-base"></i>
              </div>
              {HERO_COPY.primaryCta}
            </Link>
            <a
              href={`tel:${SITE.phoneTel}`}
              className="flex items-center justify-center gap-2 w-full px-8 py-3.5 rounded-full font-semibold text-base text-white border border-white/30 bg-white/10 backdrop-blur-sm transition-all duration-300 cursor-pointer whitespace-nowrap active:scale-95"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-base"></i>
              </div>
              {HERO_COPY.secondaryCta}
            </a>
          </div>

          {/* Dot indicators */}
          {slides.length > 1 && (
            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className="rounded-full transition-all duration-400 cursor-pointer"
                  style={{
                    width: i === active ? 24 : 7,
                    height: 7,
                    background: i === active ? current.accent_color : 'rgba(255,255,255,0.3)',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── DESKTOP LAYOUT ── */}
      <div className="hidden lg:flex relative z-10 w-full max-w-7xl mx-auto px-16 flex-row items-center justify-between gap-12 pt-24 pb-16">
        {/* LEFT: Text */}
        <div className="flex-1 flex flex-col items-start max-w-xl">
          <div style={{ minHeight: 320 }} className="flex flex-col items-start justify-center w-full">
            <div
              style={{ opacity: animating ? 0 : 1, transition: 'opacity 0.2s ease' }}
              className="flex flex-col items-start w-full"
            >
              <h1
                className="text-7xl xl:text-8xl font-black text-white leading-none mb-4 drop-shadow-2xl"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {current.name}
              </h1>
              <p className="text-white/75 text-lg leading-relaxed mb-3 max-w-md">
                {current.description}
              </p>
              <p className="text-white/45 text-sm tracking-wider mb-8">
                {current.detail}
              </p>
              <span
                className="text-4xl font-black mb-10 block transition-colors duration-700"
                style={{ color: current.accent_color }}
              >
                <PriceText price={current.price} currencyClassName="text-[0.62em] align-middle font-extrabold tracking-wide mr-0.5" />
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/menu"
              className="group flex items-center gap-3 px-10 py-4 rounded-full font-black text-lg text-black transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 shadow-2xl"
              style={{
                background: current.accent_color,
                boxShadow: `0 8px 32px ${current.accent_color}55`,
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-restaurant-line text-lg"></i>
              </div>
              {HERO_COPY.primaryCta}
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-200"></i>
              </div>
            </Link>
            <a
              href={`tel:${SITE.phoneTel}`}
              className="flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-lg text-white border border-white/30 bg-white/10 backdrop-blur-sm transition-all duration-300 cursor-pointer whitespace-nowrap hover:bg-white/20 active:scale-95"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-lg"></i>
              </div>
              {HERO_COPY.secondaryCta}
            </a>
          </div>
        </div>

        {/* RIGHT: Product image with timer ring */}
        <div
          className="flex-shrink-0 flex flex-col items-center gap-8"
          style={{ opacity: animating ? 0 : 1, transition: 'opacity 0.2s ease' }}
        >
          {/* Main product image */}
          <div
            className="relative"
            style={{ width: desktopRingSize, height: desktopRingSize }}
          >
            {/* Glow */}
            <div
              className="absolute inset-0 rounded-full blur-3xl opacity-40 transition-all duration-700"
              style={{ background: current.accent_color }}
            />
            {/* Timer ring */}
            <TimerRing
              size={desktopRingSize}
              strokeWidth={STROKE}
              color={current.accent_color}
              progress={timerProgress}
            />
            {/* Image — inset by RING_PAD so ring doesn't overlap */}
            <div
              className="absolute rounded-full overflow-hidden border-4 shadow-2xl transition-all duration-500"
              style={{
                top: RING_PAD,
                left: RING_PAD,
                width: desktopImgSize,
                height: desktopImgSize,
                borderColor: current.accent_color,
              }}
            >
              <img
                src={current.image_url}
                alt={current.name}
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Price badge */}
            <div
              className="absolute -bottom-3 -right-3 px-5 py-2.5 rounded-full text-black font-black text-base shadow-xl transition-all duration-500 z-10"
              style={{ background: current.accent_color }}
            >
              <PriceText price={current.price} currencyClassName="text-[0.78em] font-extrabold" />
            </div>
          </div>

          {/* Thumbnail strip */}
          {slides.length > 1 && (
            <div className="flex items-center gap-4">
              {slides.map((slide, i) => (
                <button
                  key={slide.id}
                  onClick={() => goTo(i)}
                  className="relative flex-shrink-0 rounded-full overflow-hidden transition-all duration-300 cursor-pointer"
                  style={{
                    width: i === active ? 68 : 50,
                    height: i === active ? 68 : 50,
                    border: i === active ? `3px solid ${current.accent_color}` : '3px solid rgba(255,255,255,0.2)',
                    opacity: i === active ? 1 : 0.55,
                    transform: i === active ? 'scale(1)' : 'scale(0.9)',
                    boxShadow: i === active ? `0 0 20px ${current.accent_color}60` : 'none',
                  }}
                >
                  {slide.image_url ? (
                    <img src={slide.image_url} alt={slide.name} className="w-full h-full object-cover object-center" />
                  ) : (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <i className="ri-image-line text-gray-400 text-sm"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Dot indicators */}
          {slides.length > 1 && (
            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className="rounded-full transition-all duration-300 cursor-pointer"
                  style={{
                    width: i === active ? 28 : 8,
                    height: 8,
                    background: i === active ? current.accent_color : 'rgba(255,255,255,0.3)',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Arrow navigation — desktop only */}
      {slides.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white transition-all duration-300 cursor-pointer backdrop-blur-sm hover:scale-110 active:scale-95"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            onClick={handleNext}
            className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white transition-all duration-300 cursor-pointer backdrop-blur-sm hover:scale-110 active:scale-95"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </>
      )}

      {/* Scroll indicator */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/35 text-sm"></i>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
